import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Wifi } from "lucide-react";

const empty = () => ({ name: "", approved_ips: "", cidrs: "", active: true });
const toList = (s) => (s || "").split(/[\n,]/).map(x => x.trim()).filter(Boolean);

export default function NetworksPanel() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = () => api.get("/hr/attendance/networks").then(r => setRows(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const openEdit = (n) => { setEditing(n); setForm({ name: n.name, approved_ips: (n.approved_ips || []).join(", "), cidrs: (n.cidrs || []).join(", "), active: n.active }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const body = { name: form.name, approved_ips: toList(form.approved_ips), cidrs: toList(form.cidrs), active: form.active };
    try {
      if (editing) await api.put(`/hr/attendance/networks/${editing.id}`, body);
      else await api.post("/hr/attendance/networks", body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete network?")) return;
    try { await api.delete(`/hr/attendance/networks/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid="att-networks-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">Approved office networks. A punch passes network if the backend-observed IP matches an approved IP or CIDR range.</p>
        <Button data-testid="att-net-new" onClick={() => { setEditing(null); setForm(empty()); setOpen(true); }} className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-1.5" />Network</Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {rows.map(n => (
          <div key={n.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`att-net-${n.id}`}>
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold flex items-center gap-2"><Wifi className="w-4 h-4 text-sky-600" />{n.name}</h4>
              <span className="flex gap-1">
                <button onClick={() => openEdit(n)} data-testid={`att-net-edit-${n.id}`}><Pencil className="w-4 h-4 text-slate-400" /></button>
                <button onClick={() => del(n.id)}><Trash2 className="w-4 h-4 text-rose-500" /></button>
              </span>
            </div>
            {(n.approved_ips || []).length > 0 && <div className="text-xs text-slate-500 font-mono">IPs: {n.approved_ips.join(", ")}</div>}
            {(n.cidrs || []).length > 0 && <div className="text-xs text-slate-500 font-mono">CIDR: {n.cidrs.join(", ")}</div>}
            <Badge variant="outline" className={`mt-1.5 ${n.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-400"}`}>{n.active ? "Active" : "Inactive"}</Badge>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-2 rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-slate-400 text-sm">No networks yet</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="att-net-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit network" : "New network"}</DialogTitle>
            <DialogDescription>Approved office IPs/CIDRs. The backend-observed IP must match for a punch to pass the network check.</DialogDescription></DialogHeader>
          <div><Label>Network Name *</Label><Input data-testid="att-net-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Office WiFi" /></div>
          <div><Label>Approved IPs (comma / newline separated)</Label><Input data-testid="att-net-ips" value={form.approved_ips} onChange={e => setForm({ ...form, approved_ips: e.target.value })} placeholder="203.0.113.10, 203.0.113.11" /></div>
          <div><Label>CIDR ranges (comma / newline separated)</Label><Input data-testid="att-net-cidrs" value={form.cidrs} onChange={e => setForm({ ...form, cidrs: e.target.value })} placeholder="203.0.113.0/24" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} data-testid="att-net-active" />Active</label>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="att-net-save" className="bg-slate-900">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
